import { useState, useEffect } from 'react';
import { Users, Sparkles, X } from 'lucide-react';
import { BottomSheet } from './Overlay';
import api from '../lib/api';

/**
 * CommunityCreateSheet — Q28
 *
 * Used in two flavours:
 *   • Direct: opened from Communities listing → fully blank form.
 *   • From-plan promotion: opened from PlanDetails/Activity prompt →
 *     plan id pre-loaded; name + city + category inherit from the plan.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - onCreated (community) — callback after success (e.g. navigate to profile)
 *  - sourcePlan (plan | null) — pre-fills + sets plan_origin_id
 */
export default function CommunityCreateSheet({ open, onClose, onCreated, sourcePlan }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Hydrate the inputs whenever the sheet opens — `sourcePlan` often arrives
  // AFTER the parent first mounts the sheet (e.g. PlanDetails fetches the
  // promote-prompt async). `useState(initial)` only fires once, so we sync
  // via effect to handle late prop updates.
  useEffect(() => {
    if (!open) return;
    setName(sourcePlan ? `${sourcePlan.title || ''} Club` : '');
    setDescription('');
    setError('');
  }, [open, sourcePlan]);

  const submit = async () => {
    setError('');
    if (!name.trim()) return setError('Community name is required');
    setBusy(true);
    try {
      const { data } = await api.post('/communities', {
        name: name.trim(),
        description: description.trim(),
        city: sourcePlan?.city || '',
        category: sourcePlan?.category || '',
        cover_image: sourcePlan?.cover_image || '',
        plan_origin_id: sourcePlan?.id || '',
      });
      onCreated?.(data);
      setName(''); setDescription('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create community');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) onClose?.(); }}
      title={sourcePlan ? 'Keep this community alive?' : 'Start a community'}
      footer={
        <button
          data-testid="community-create-submit"
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Sparkles size={14} />
          {busy ? 'Creating…' : (sourcePlan ? 'Yes, create the community' : 'Create community')}
        </button>
      }
    >
      <div data-testid="community-create-form" className="space-y-3.5">
        {sourcePlan && (
          <div className="bg-[#B6E600]/12 border border-[#B6E600]/35 rounded-2xl p-3 flex items-center gap-3">
            <Users size={16} className="text-[#3F5200]" />
            <p className="text-xs text-[#3F5200] leading-relaxed">
              Promote <strong>{sourcePlan.title}</strong> into a permanent
              community. {sourcePlan.approved_count || 0} attendees become charter
              members, and your existing chat stays intact.
            </p>
          </div>
        )}

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Community name <span className="text-[#FF3B30]">*</span>
          </label>
          <input
            data-testid="community-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Sunset Run Club"
            className="biz-input"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Description
          </label>
          <textarea
            data-testid="community-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="What's this community about?"
            className="biz-input resize-none"
          />
        </div>

        {error && (
          <div data-testid="community-create-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-3 py-2 text-[#FF3B30] text-xs">
            {error}
          </div>
        )}
      </div>
      <style>{`
        .biz-input {
          width: 100%; background:#fff; color:#111; border-radius:14px;
          padding:10px 12px; font-size:14px; border:1px solid rgba(0,0,0,0.06);
        }
        .biz-input:focus { outline:none; border-color: rgba(182,230,0,0.45); }
      `}</style>
    </BottomSheet>
  );
}
